import React, { useState, useEffect, useRef } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';

/**
 * LiveVideoFeed - Memoized video preview component
 * Prevents unnecessary re-renders that could disrupt the video stream
 */
const LiveVideoFeed = React.memo(React.forwardRef(({ previewStream }, ref) => {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (videoRef.current && previewStream) {
      // Only set srcObject if it's different to avoid unnecessary updates
      if (videoRef.current.srcObject !== previewStream) {
        videoRef.current.srcObject = previewStream;
        // Attempt to play the video once srcObject is set
        videoRef.current.play().catch(() => {
          // Silently handle play errors (e.g., browser autoplay policies)
        });
      }
    }
  }, [previewStream]);

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  React.useImperativeHandle(ref, () => ({
    getVideoElement: () => videoRef.current
  }));

  return (
    <video 
      ref={videoRef}
      autoPlay 
      playsInline 
      muted 
      className="w-full h-full object-cover"
      style={{ transform: 'scaleX(-1)' }}
    />
  );
}));

/**
 * TimerDisplay - Isolated timer component
 * Manages its own state to prevent parent re-renders during countdown
 */
const TimerDisplay = ({ isRecording, maxTime }) => {
  const [time, setTime] = useState(0);
  
  useEffect(() => {
    if (!isRecording) {
      setTime(0);
      return;
    }
    
    const interval = setInterval(() => {
      setTime(t => t + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isRecording]);
  
  if (!isRecording) return null;
  
  const remainingTime = maxTime - time;
  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;
  
  return (
    <div className="absolute top-4 right-4 flex items-center bg-red-500 text-white px-3 py-1 rounded-full pointer-events-none z-20">
      <div className="w-3 h-3 bg-white rounded-full mr-2 animate-pulse"></div>
      <span>Recording {minutes}:{String(seconds).padStart(2, '0')}</span>
    </div>
  );
};

/**
 * VideoRecorder - Main video recording component
 * 
 * Architecture notes:
 * - Uses react-media-recorder for media handling
 * - TimerDisplay is isolated to prevent re-renders during recording
 * - Countdown state causes temporary re-renders (known limitation)
 * - Parent component can force remount via key prop for "Record Again"
 */
function VideoRecorder({ 
  value, 
  onChange, 
  disabled = false, 
  maxRecordingTime = 120, 
  isSaving = false,
  showCountdown = true,
  countdownSeconds = 5,
  height = '360px'
}) {
  const autoStopTimerRef = useRef(null);
  const [countdown, setCountdown] = useState(0);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [maxTimeReached, setMaxTimeReached] = useState(false);
  const onChangeCalledRef = useRef(null);

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    previewStream,
    clearBlobUrl
  } = useReactMediaRecorder({
    video: { 
      facingMode: 'user',
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 24 }
    },
    audio: true,
    askPermissionOnMount: true,
    blobPropertyBag: { type: 'video/webm' },
    mediaRecorderOptions: {
      audioBitsPerSecond: 128000,
      videoBitsPerSecond: 800000,
      mimeType: 'video/webm'
    }
  });

  // Auto-stop recording after max time
  // Uses setTimeout instead of setInterval to avoid state updates during recording
  useEffect(() => {
    if (status === 'recording') {
      autoStopTimerRef.current = setTimeout(() => {
        setMaxTimeReached(true);
        stopRecording();
      }, maxRecordingTime * 1000);
    } else {
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current);
        autoStopTimerRef.current = null;
      }
    }

    return () => {
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current);
      }
    };
  }, [status, stopRecording, maxRecordingTime]);

  // Update parent component when video is recorded
  useEffect(() => {
    if (mediaBlobUrl && onChange && onChangeCalledRef.current !== mediaBlobUrl) {
      onChangeCalledRef.current = mediaBlobUrl;
      onChange(mediaBlobUrl);
    }
  }, [mediaBlobUrl, onChange]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (previewStream) {
        const tracks = previewStream.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, [previewStream]);

  // Countdown effect
  useEffect(() => {
    if (!isCountingDown || countdown <= 0) return;

    const timer = setTimeout(() => {
      if (countdown === 1) {
        setIsCountingDown(false);
        setCountdown(0);
        startRecording();
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isCountingDown, countdown, startRecording]);

  const handleStartRecording = () => {
    setMaxTimeReached(false);
    
    if (showCountdown) {
      setIsCountingDown(true);
      setCountdown(countdownSeconds);
    } else {
      startRecording();
    }
  };

  const handleStopRecording = () => {
    stopRecording();
  };

  const handleRecordAgain = () => {
    clearBlobUrl();
    setMaxTimeReached(false);
    onChangeCalledRef.current = null;
    if (onChange) {
      // Setting value to empty triggers parent (VideoAnswerQuestion) to remount via key change
      onChange('');
    }
  };

  const getBorderColor = () => {
    if (status === 'recording') return 'border-red-500';
    if (mediaBlobUrl) return 'border-[#33C6B6]';
    return 'border-gray-300';
  };

  return (
    <div className="w-full">
      <div className="w-full">
        <div className={`relative w-full rounded-lg border ${getBorderColor()} bg-gray-50 p-4 flex flex-col justify-between`} style={{ minHeight: height }}>
          {!mediaBlobUrl || isSaving ? (
            <>
              {/* Video Preview Area */}
              <div className="flex-1 flex items-center justify-center">
                {status === 'acquiring_media' ? (
                  <div className="text-gray-500 text-center">
                    <svg className="w-12 h-12 mx-auto mb-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p>Getting camera access...</p>
                  </div>
                ) : (
                  <div className="w-full rounded-lg bg-black overflow-hidden relative" style={{ height }}>
                    <LiveVideoFeed previewStream={previewStream} />
                    <TimerDisplay isRecording={status === 'recording'} maxTime={maxRecordingTime} />
                    {maxTimeReached && (
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white px-4 py-2 rounded-full">
                        Maximum recording time reached
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Button Area */}
              <div className="flex justify-center mt-4 mb-2">
                {status !== 'recording' && !isCountingDown && !isSaving ? (
                  <button 
                    type="button"
                    onClick={handleStartRecording}
                    disabled={disabled || status === 'acquiring_media'}
                    className={`px-6 py-3 text-white font-medium rounded-lg transition-colors duration-200 shadow-md flex items-center ${
                      disabled || status === 'acquiring_media' ? 'opacity-50 cursor-not-allowed bg-gray-400' : 'hover:opacity-90'
                    }`}
                    style={{ backgroundColor: disabled || status === 'acquiring_media' ? undefined : '#33C6B6' }}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                    {status === 'acquiring_media' ? 'Getting Camera...' : 'Record Answer'}
                  </button>
                ) : isCountingDown ? (
                  <button 
                    disabled
                    className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium rounded-lg opacity-75 cursor-not-allowed shadow-md flex items-center"
                  >
                    <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                    Starting in {countdown}...
                  </button>
                ) : (
                  <button 
                    type="button"
                    onClick={handleStopRecording}
                    disabled={disabled || isSaving}
                    className={`px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg hover:from-red-600 hover:to-red-700 transition-colors duration-200 shadow-md flex items-center ${
                      disabled || isSaving ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="6" width="12" height="12" strokeWidth="2" />
                        </svg>
                        Stop Recording
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Video Playback Area */}
              <div className="flex-1 flex items-center justify-center">
                <video 
                  src={mediaBlobUrl}
                  controls 
                  autoPlay
                  playsInline
                  className="w-full rounded-lg"
                  style={{ height }}
                  preload="metadata"
                />
              </div>
              
              {/* Button Area */}
              <div className="flex justify-center mt-4 mb-2">
                {isSaving ? (
                  <button 
                    type="button"
                    disabled={true}
                    className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-md flex items-center opacity-50 cursor-not-allowed"
                  >
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </button>
                ) : (
                  <button 
                    type="button"
                    onClick={handleRecordAgain}
                    disabled={disabled}
                    className={`px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors duration-200 shadow-md flex items-center ${
                      disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Record Again
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
    </div>
  );
}

export default React.memo(VideoRecorder);
